import { ref, Ref, inject } from 'vue';
import { FetchMFAPreferenceOutput } from 'aws-amplify/auth';
import timezone from '@/models/global/timezone';
import { Organization } from '@/models/Organization';

type MFAType = 'SMS' | 'EMAIL' | 'TOTP';

export type UserMFAState = {
  enabled: MFAType[];
  preferred?: MFAType;
};

// 型定義
export interface CurrentUser {
  username: string;
  email: string;
  isTester: boolean;
  isAdmin: boolean;
  // 現在の組織で接続先設定権限による設定可能なapp nameを持つか
  canConfigureConnections: boolean;
  userLabel: string;
  cognitoId: string;
  organizations: Organization[];
  features: string[];
  imageUrl: string;
  timezone: string;
  groups: string[];
  mfaState: UserMFAState | null;
}

// Provider
export const createCurrentUser = () => {
  const currentUser: Ref<CurrentUser> = ref({
    username: '',
    email: '',
    isTester: false,
    cognitoId: '',
    organizations: [],
    features: [],
    imageUrl: '',
    timezone: '',
    isAdmin: false,
    canConfigureConnections: false,
    userLabel: '',
    groups: [],
    mfaState: null,
  } as CurrentUser);
  return {
    currentUser,
  };
};

// Inject
export const keyCurrentUser = Symbol();
export const useCurrentUser = () => {
  const { currentUser } = inject(keyCurrentUser) as {
    currentUser: Ref<CurrentUser>;
  };

  const setCurrentUser = (user: CurrentUser) => {
    const model = {
      username: user.username,
      email: user.email,
      // 特定アカウントはテスターになる
      isTester:
        user.username === 'tester' || user.email?.split('@')[1] === 'demo.srest'
          ? true
          : false,
      isAdmin: false, // updateIsAdminで更新する
      canConfigureConnections: false, // updateIsAdminで更新する
      userLabel:
        user.userLabel || currentUser.value.organizations[0]?.userLabel || '',
      // cognitoIdは、setCurrentUserで変更されない
      // cognitoから直接受け取るのではなくapp_namesから受け取ることでログイン
      // 切り替えを検知する仕組み
      cognitoId: currentUser.value.cognitoId,
      // organizationは、setCurrentUserで変更されない
      organizations: currentUser.value.organizations,
      groups: user.groups || [],
      imageUrl: user.imageUrl,
      timezone: user.timezone,
      mfaState: currentUser.value.mfaState,
      features: currentUser.value.features || [],
    };
    currentUser.value = model;
    timezone.commit('setTimezone', user.timezone);
    return;
  };

  const updateIsAdmin = (targetOrganizationLabel: string) => {
    const targetOrganization = currentUser.value.organizations.find(
      (org) => org.label === targetOrganizationLabel
    );
    if (!targetOrganization) {
      currentUser.value.isAdmin = false;
      currentUser.value.canConfigureConnections = false;
      currentUser.value.features = [];
      return;
    }
    currentUser.value.isAdmin = targetOrganization.roles.some(
      (role) => role.administrator === true
    );
    currentUser.value.canConfigureConnections =
      targetOrganization.configurableAppNames.length > 0;
    currentUser.value.features = targetOrganization.features.map((f) => f.code);
  };

  const updateMFAState = (mfaPreference: FetchMFAPreferenceOutput) => {
    const enabled = mfaPreference.enabled ?? [];
    currentUser.value.mfaState = {
      enabled,
      preferred: mfaPreference.preferred,
    };
  };

  const setOrganizations = (organizations: Organization[]) => {
    currentUser.value.organizations = organizations;
    currentUser.value.userLabel = organizations[0]?.userLabel || '';
  };
  const setCognitoId = (cognitoId: string) => {
    currentUser.value.cognitoId = cognitoId;
  };
  const clearCurrentUser = () => {
    currentUser.value = {
      username: '',
      email: '',
      isTester: false,
      cognitoId: '',
      organizations: [],
      isAdmin: false,
      canConfigureConnections: false,
      userLabel: '',
      groups: [],
      features: [],
      imageUrl: '',
      timezone: '',
      mfaState: null,
    };
  };
  return {
    currentUser: currentUser,
    setCurrentUser,
    setOrganizations,
    setCognitoId,
    clearCurrentUser,
    updateIsAdmin,
    updateMFAState,
  };
};
